import ExcelJS from 'exceljs';
import { formatGuideEntryDateTimeLima } from './dateUtils';
import { documentCurrencyLabel } from './documentCurrency';

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD1FAE5' },
};

const TAB_LABELS = {
  entrada: 'Guias_entrada',
  salida: 'Guias_salida',
  inicial: 'Guias_iniciales',
  traspaso: 'Traspasos',
  recepcion: 'Recepciones',
  ajuste: 'Ajustes',
};

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function formatExportDate() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function sanitizeFilenamePart(value) {
  return String(value || 'almacen')
    .replace(/[^\w\-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 40);
}

function styleHeaderRow(row, labels) {
  labels.forEach((label, i) => {
    const cell = row.getCell(i + 1);
    cell.value = label;
    cell.font = { bold: true };
    cell.fill = HEADER_FILL;
  });
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatReferences(doc) {
  const refs = Array.isArray(doc.references) ? doc.references : [];
  return {
    labels: refs.map((r) => r.label).filter(Boolean).join('; '),
    codes: refs.map((r) => r.code).filter(Boolean).join('; '),
  };
}

function splitDateTime(doc) {
  const formatted = formatGuideEntryDateTimeLima(doc.entry_date, doc.entry_time);
  if (!formatted || formatted === '—') {
    return { date: '', time: '' };
  }
  const parts = formatted.split(' ');
  if (parts.length >= 2) {
    return { date: parts[0], time: parts.slice(1).join(' ') };
  }
  return { date: formatted, time: '' };
}

function statusLabel(status) {
  if (status === 'completed' || status === 'issued') return 'Completado';
  if (status === 'cancelled') return 'Cancelado';
  if (status === 'pending') return 'Pendiente';
  return status || '';
}

function buildEntradaRows(guides) {
  return guides.map((doc) => {
    const { date, time } = splitDateTime(doc);
    const refs = formatReferences(doc);
    const totalAmount = toNumber(doc.total_amount) ?? 0;
    const invoiced = toNumber(doc.billing_invoiced_amount);
    const pending = toNumber(doc.billing_pending_amount);
    const referenceAmount = toNumber(doc.billing_reference_amount);
    const difference = invoiced != null ? totalAmount - invoiced : null;

    return {
      values: [
        doc.code || '',
        documentCurrencyLabel(doc.currency),
        date,
        time,
        doc.supplier || '',
        refs.labels,
        refs.codes,
        doc.items_count ?? 0,
        totalAmount,
        referenceAmount,
        invoiced,
        pending,
        difference,
        doc.created_by || '',
      ],
      difference,
    };
  });
}

function buildSimpleEntityRows(guides) {
  return guides.map((doc) => {
    const { date, time } = splitDateTime(doc);
    return {
      values: [
        doc.code || '',
        date,
        time,
        doc.supplier || '',
        doc.items_count ?? 0,
        statusLabel(doc.status),
        doc.created_by || '',
      ],
      difference: null,
    };
  });
}

function buildSalidaRows(guides) {
  return guides.map((doc) => {
    const { date, time } = splitDateTime(doc);
    return {
      values: [
        doc.code || '',
        date,
        time,
        doc.supplier || '',
        doc.reception_source_code || '',
        doc.items_count ?? 0,
        statusLabel(doc.status),
        doc.created_by || '',
      ],
      difference: null,
    };
  });
}

function buildTraspasoRows(guides) {
  return guides.map((doc) => {
    const { date, time } = splitDateTime(doc);
    return {
      values: [
        doc.code || '',
        date,
        time,
        doc.from_warehouse || '',
        doc.to_warehouse || '',
        toNumber(doc.completion_percentage) ?? 0,
        doc.items_count ?? 0,
        statusLabel(doc.status),
        doc.created_by || '',
      ],
      difference: null,
    };
  });
}

function buildRecepcionRows(guides) {
  return guides.map((doc) => {
    const { date, time } = splitDateTime(doc);
    return {
      values: [
        doc.code || '',
        date,
        time,
        doc.from_warehouse || '',
        doc.to_warehouse || '',
        doc.transfer_guide_code || '',
        doc.is_consumed ? 'Sí' : 'No',
        doc.consume_exit_guide_code || '',
        doc.items_count ?? 0,
        statusLabel(doc.status),
        doc.created_by || '',
      ],
      difference: null,
    };
  });
}

const EXPORT_CONFIG = {
  entrada: {
    sheetName: 'Entrada',
    title: 'Guías de entrada del almacén. Incluye montos de guía vs facturación para revisar diferencias.',
    headers: [
      'Código',
      'Moneda',
      'Fecha',
      'Hora',
      'Entidad',
      'Referencia',
      'Cód. documento',
      'Items',
      'Monto guía',
      'Ref. facturación',
      'Monto facturado',
      'Monto pendiente',
      'Diferencia (guía - facturado)',
      'Usuario',
    ],
    amountColumns: [9, 10, 11, 12, 13],
    differenceColumn: 13,
    buildRows: buildEntradaRows,
  },
  inicial: {
    sheetName: 'Iniciales',
    title: 'Guías iniciales del almacén.',
    headers: ['Código', 'Fecha', 'Hora', 'Entidad', 'Items', 'Estado', 'Usuario'],
    amountColumns: [],
    differenceColumn: null,
    buildRows: buildSimpleEntityRows,
  },
  ajuste: {
    sheetName: 'Ajustes',
    title: 'Guías de ajuste del almacén.',
    headers: ['Código', 'Fecha', 'Hora', 'Entidad', 'Items', 'Estado', 'Usuario'],
    amountColumns: [],
    differenceColumn: null,
    buildRows: buildSimpleEntityRows,
  },
  salida: {
    sheetName: 'Salida',
    title: 'Guías de salida del almacén.',
    headers: ['Código', 'Fecha', 'Hora', 'Entidad', 'Recep. origen', 'Items', 'Estado', 'Usuario'],
    amountColumns: [],
    differenceColumn: null,
    buildRows: buildSalidaRows,
  },
  traspaso: {
    sheetName: 'Traspasos',
    title: 'Traspasos del almacén.',
    headers: ['Código', 'Fecha', 'Hora', 'Desde', 'Hacia', '% completado', 'Items', 'Estado', 'Usuario'],
    amountColumns: [6],
    differenceColumn: null,
    buildRows: buildTraspasoRows,
  },
  recepcion: {
    sheetName: 'Recepciones',
    title: 'Recepciones del almacén.',
    headers: [
      'Código',
      'Fecha',
      'Hora',
      'Desde',
      'Hacia',
      'Guía traspaso',
      'Consumida',
      'Guía consumo',
      'Items',
      'Estado',
      'Usuario',
    ],
    amountColumns: [],
    differenceColumn: null,
    buildRows: buildRecepcionRows,
  },
};

/**
 * Exporta el listado de guías del almacén (respeta pestaña y filtros aplicados).
 */
export async function downloadWarehouseGuidesExcel({
  guides = [],
  activeTab = 'entrada',
  warehouse = null,
  guideFilter = '',
} = {}) {
  const config = EXPORT_CONFIG[activeTab] || EXPORT_CONFIG.entrada;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(config.sheetName, { views: [{ state: 'frozen', ySplit: 3 }] });

  const lastCol = String.fromCharCode('A'.charCodeAt(0) + config.headers.length - 1);
  ws.mergeCells(`A1:${lastCol}1`);
  ws.getCell('A1').value = config.title;
  ws.getCell('A1').alignment = { wrapText: true, vertical: 'middle' };

  ws.mergeCells(`A2:${lastCol}2`);
  const filterNote = guideFilter.trim()
    ? `Filtro: "${guideFilter.trim()}" · ${guides.length} registro(s)`
    : `${guides.length} registro(s)`;
  ws.getCell('A2').value = [
    warehouse?.name ? `Almacén: ${warehouse.name}` : null,
    warehouse?.code ? `(${warehouse.code})` : null,
    filterNote,
  ].filter(Boolean).join(' · ');
  ws.getCell('A2').font = { italic: true, color: { argb: 'FF4B5563' } };

  ws.getRow(1).height = 28;
  ws.getRow(2).height = 20;
  styleHeaderRow(ws.getRow(3), config.headers);

  const builtRows = config.buildRows(guides);
  builtRows.forEach(({ values, difference }) => {
    const row = ws.addRow(values);
    config.amountColumns.forEach((colIndex) => {
      const cell = row.getCell(colIndex);
      if (cell.value !== null && cell.value !== '' && cell.value !== undefined) {
        cell.numFmt = '#,##0.00';
      }
    });
    if (config.differenceColumn && difference != null && Math.abs(difference) > 0.01) {
      const diffCell = row.getCell(config.differenceColumn);
      diffCell.font = { color: { argb: 'FFB45309' }, bold: true };
    }
  });

  ws.columns = config.headers.map((header, index) => {
    if (header === 'Entidad' || header === 'Referencia') return { width: 28 };
    if (header === 'Código' || header === 'Cód. documento') return { width: 14 };
    if (index === 0) return { width: 12 };
    return { width: 16 };
  });

  const buf = await wb.xlsx.writeBuffer();
  const warehousePart = sanitizeFilenamePart(warehouse?.code || warehouse?.name);
  const tabPart = TAB_LABELS[activeTab] || 'guias';
  downloadBuffer(buf, `${tabPart}_${warehousePart}_${formatExportDate()}.xlsx`);
}
